import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppHeader,
  Button,
  Icon,
  OrbFan,
  WorldSwitcher,
  type HottrProduct,
} from '@/kit';
import { routes } from '@/config/routes';

/**
 * The shared HOTTR chassis for `.me`, mounted once around the authed app.
 *
 * Screen System §1 made real: no bottom tab bar, no centre nav, no hamburger.
 * The Orb is the single persistent control — tap it for the routes, hold it for
 * Hottie — and the header owns identity and the world switcher, never
 * navigation.
 *
 * `.me` previously carried three navigations at once: a handmade 56px mobile
 * top bar, the kit header in `nav-center` layout, and a handmade mobile bottom
 * tab bar. All three are gone. What replaced them is one control at two
 * densities are gone — OrbFan is the one control, at every width, reading the route
 * vocabulary out of kit/worlds.ts, so the ordering and the fixed WORLDS tail
 * are identical here and on every sibling site.
 *
 * The switcher takes no `worlds` and no `hrefFor`. The kit defaults to
 * SWITCHABLE_WORLDS and the canonical domain for each, and hardcoding either here is
 * how nine apps ended up with nine opinions about where .music lives — and how
 * Passport and the brand site ended up as 404 links.
 *
 * Canon `WORLDS.me` carries its rooms in kit/worlds.ts — `today`,
 * `breakout`, `momentum` — and the fixed WORLDS · HOTTIE tail lands under
 * them. The consumer adds nothing at runtime; one kit sync delivers it.
 *
 * There is no `onHottie`. `.me` has no assistant of its own (Settings holds
 * share-permission toggles, not a chat), so both the HOTTIE tail item and a
 * long press fall through to the kit default: the one ecosystem Hottie on
 * HOTTR.app. That is the intended behaviour everywhere except HOTTR.app itself.
 *
 * Orb suppression on the pre-login surfaces is structural rather than a
 * blocklist: App.tsx mounts AppShell — and therefore this chassis — only inside
 * the RequireAuth + RequireOnboarded branch, so `/`, `/onboarding`, `/auth` and
 * `/auth/callback` never render an Orb. §6: navigation does not exist until you
 * are in, and the Orb's first appearance is the moment the product starts.
 */

const PRODUCT: HottrProduct = 'me';

/**
 * The world's vocabulary mapped onto this app's real paths.
 *
 * The route NAMES are canon (kit/worlds.ts) and are the same words a reader
 * sees on .travel or .music. The paths are this app's business — which is
 * exactly the seam the Screen System intends: same chassis, same words, the
 * product decides where they go.
 *
 * today / breakout / momentum are this app's rooms. They are listed so a later
 * brand sync that adds those names to `WORLDS.me.routes` lights the fan
 * without another consumer invention. Until then OrbFan never calls
 * them — in-page CTAs remain the way between rooms.
 *
 * PASSPORT points at Settings because that is where `.me` keeps identity and
 * the share-permission toggles — the same word every sibling site uses for the
 * same job, landing wherever that product happens to keep it.
 */
const PATHS: Record<string, string> = {
  today: routes.today,
  breakout: routes.breakout,
  momentum: routes.momentum,
  passport: routes.settings,
};

/**
 * Glyphs for the three `.me`-only rooms. ROUTE_ICONS has `today` (from .care)
 * but no breakout / momentum, and the kit renders a bare label rather than a
 * plausible wrong icon, so the app supplies its own. Unused until canon lists
 * those routes.
 *
 * `bolt` is deliberately NOT used here even though it fits Momentum: in this
 * app bolt already means "I'm Close", on the header button and on Today's
 * decision-point card. One glyph, one meaning.
 */
const ROUTE_GLYPHS: Record<string, string> = {
  today: 'today',
  breakout: 'flag',
  momentum: 'trending_up',
};

/** Which route the current URL corresponds to, for the pink active state. */
function activeRouteFor(pathname: string): string {
  if (pathname.startsWith(routes.today)) return 'today';
  if (pathname.startsWith(routes.breakout)) return 'breakout';
  if (pathname.startsWith(routes.momentum)) return 'momentum';
  if (pathname.startsWith(routes.settings)) return 'passport';
  // `/im-close` intentionally falls through to no active route. It is a
  // decision-point action reached from a CTA, not a destination in the fan.
  return '';
}

export function HottrChassis({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeRoute = useMemo(() => activeRouteFor(pathname), [pathname]);

  const go = (route: string) => {
    const to = PATHS[route];
    if (to) navigate(to);
  };

  const imClose = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => navigate(routes.imClose)}
      leading={<Icon name="bolt" className="text-[18px]" />}
    >
      I&apos;m Close
    </Button>
  );

  return (
    <div className="min-h-dvh bg-hottr-ink text-white" data-hand="right">
      {/*
        One header, at both breakpoints. `mobile` draws the 56px bar that the
        handmade one used to, which is why that handmade bar could go: the
        header's centre slot hides itself below md, so this is identity plus the
        I'm Close action and nothing else — no navigation.

        `items={[]}` and no `layout` prop: centre nav is banned outright (§1),
        so the header keeps identity and utility only.

        The WorldSwitcher occupies the wordmark's slot rather than sitting
        beside it — §2 says the suffix IS the control, so this replaces
        furniture instead of adding it.
      */}
      <AppHeader
        surface="me"
        homeTo={routes.today}
        items={[]}
        mobile
        switcher={
          <WorldSwitcher
            current={PRODUCT}
            onSelect={(product) => {
              if (product === PRODUCT) navigate(routes.today);
            }}
          />
        }
        utility={imClose}
      />

      <div className="flex">
        <main className="min-w-0 flex-1">
          {/* pb-28 is Orb clearance, not decoration: the mobile Orb is 56px at
              a 20px inset, so 112px keeps it clear of Today's full-width
              "I'm Close" decision-point button at the foot of the page. There
              is no lg takeover: the same Orb is the control at every width. */}
          <div className="mx-auto w-full max-w-3xl px-4 pb-28 pt-6 md:px-6 lg:pb-10">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile: the Orb and its fan, hidden at desktop widths by chassis.css. */}
      <OrbFan
        product={PRODUCT}
        activeRoute={activeRoute}
        onNavigate={go}
        routeIcons={ROUTE_GLYPHS}
      />
    </div>
  );
}
